import React, {useCallback, useMemo, useRef} from 'react';
import SMCModal from '../../components/core/SMCModal';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCButton from '../../components/Simple/SMCButton';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Api from '../../api/SMPFetchHelper';
import {useSMCSnackbar, useBoolean} from '../../utils/hooks';
import {useSMCErrorHandler} from '../../utils/errorUtils';
import TextInputModal from './TextInputModal';
import {validateEmailAddress} from '../../utils/utils';
import {ObjectTypes} from '../../api/consts';
import VerifyCaptchaModal from './VerifyCaptchaModal';
import QRCode from 'react-qr-code';

function ShowQRModal({
    isOpen = false,
    onRequestClose,
    qrLink,
    objectType,
    userId,
    userEmail
}) {
    const { t } = useTranslation();
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSMCSnackbar();
    const { getErrorMessage } = useSMCErrorHandler();

    const customEmailRef = useRef(null);
    const [isEmailInputOpen, openEmailInput, closeEmailInput] = useBoolean(false);
    const [isCaptchaModalOpen, openCaptchaModal, closeCaptchaModal] = useBoolean(false);
    const qrLinkData = useMemo(() => {
        const encodedData = qrLink?.split('&chl=')?.[1]?.split('&')?.[0];
        if (!encodedData) {
            return undefined;
        }
        return decodeURIComponent(encodedData);
    }, [qrLink]);

    const handleFinishCaptcha = useCallback(captchaKey => {
        let userType;
        if (objectType === ObjectTypes.USER) {
            userType = 'User';
        } else if (objectType === ObjectTypes.ADMINISTRATOR) {
            userType = 'Administrator';
        }
        const emailAddress = userEmail || customEmailRef.current;
        closeCaptchaModal();
        Api.sendUserTwoFAMail(
            userType,
            userId,
            emailAddress,
            captchaKey
        )
        .then(() => {
            enqueueSuccessSnackbar('QR code has been sent to the user by email');
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            enqueueErrorSnackbar(errorMessage);
        })
    }, [userId, userEmail, objectType, closeCaptchaModal, getErrorMessage, enqueueErrorSnackbar, enqueueSuccessSnackbar]);

    const handleEmailEntered = useCallback((emailAddress) => {
        if (!validateEmailAddress(emailAddress)) {
            alert(t('com.sofaware.schema.ATTRIBUTE_INVALID_VALUE_EMAIL', [emailAddress, t('SMC_USER_EMAIL')]));
        } else {
            customEmailRef.current = emailAddress;
            closeEmailInput();
            openCaptchaModal();
        }
    }, [closeEmailInput, openCaptchaModal, t]);

    const handleSendMail = useCallback(() => {
        if (!userEmail) {
            openEmailInput();
        } else {
            openCaptchaModal();
        }
    }, [userEmail, openEmailInput, openCaptchaModal]);

    return (
        <SMCModal
            isOpen={isOpen}
            width={420}
            height={440}
            title="QR CODE"
            onRequestClose={onRequestClose}
        >
            <SMCModalBody>
                <QRCode level='M' value={qrLinkData} style={{ width: '300px', height: '300px', right: 'auto', left: '60px', top: '6.5px', margin: '0px', position: 'absolute' }} />
            </SMCModalBody>
            <SMCModalButtonContainer
                left={
                    <SMCButton onClick={handleSendMail}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="#2E5C91" icon="paper-plane" />
                        {t('SMC_GEN_SEND_MAIL')}
                    </SMCButton>
                }
                right={
                    <SMCButton onClick={onRequestClose}>
                        <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                        {t('SMC_GEN_CLOSE')}
                    </SMCButton>
                }
            />
            {/* Floating windows */}
            <TextInputModal
                isOpen={isEmailInputOpen}
                onRequestClose={closeEmailInput}
                onSuccess={handleEmailEntered}
                textInputLabel={t('SMC_USER_EMAIL')}
                title="Enter email"
                labelWidth={130}
                width={400}
            />
            <VerifyCaptchaModal
                isOpen={isCaptchaModalOpen}
                onRequestClose={closeCaptchaModal}
                onFinish={handleFinishCaptcha}
            />
        </SMCModal>

    );
}

export default ShowQRModal;